/**
 * WMO weather interpretation codes — single source of truth shared
 * between backend (Go imports the JSON equivalent) and frontend.
 *
 * Reference: https://open-meteo.com/en/docs (WMO weather interpretation)
 *
 * Each entry exposes:
 *   - `key`      : i18n key under `weather.wmo.*`
 *   - `category` : coarse bucket used for icon mapping
 *
 * Icons (emoji or Lucide) are resolved per-style in the frontend
 * (`lib/weather-icons.ts`) so this module stays presentation-agnostic.
 */

export type WeatherCategory =
	| "clear"
	| "mainly_clear"
	| "partly_cloudy"
	| "overcast"
	| "fog"
	| "drizzle"
	| "rain"
	| "freezing_rain"
	| "snow"
	| "snow_grains"
	| "rain_showers"
	| "snow_showers"
	| "thunderstorm"
	| "thunderstorm_hail";

export type WmoEntry = {
	key: string;
	category: WeatherCategory;
};

export const WMO_CODES: Record<number, WmoEntry> = {
	0: { key: "weather.wmo.clear", category: "clear" },
	1: { key: "weather.wmo.mainly_clear", category: "mainly_clear" },
	2: { key: "weather.wmo.partly_cloudy", category: "partly_cloudy" },
	3: { key: "weather.wmo.overcast", category: "overcast" },
	45: { key: "weather.wmo.fog", category: "fog" },
	48: { key: "weather.wmo.fog_rime", category: "fog" },
	51: { key: "weather.wmo.drizzle_light", category: "drizzle" },
	53: { key: "weather.wmo.drizzle_moderate", category: "drizzle" },
	55: { key: "weather.wmo.drizzle_dense", category: "drizzle" },
	56: { key: "weather.wmo.freezing_drizzle_light", category: "freezing_rain" },
	57: { key: "weather.wmo.freezing_drizzle_dense", category: "freezing_rain" },
	61: { key: "weather.wmo.rain_light", category: "rain" },
	63: { key: "weather.wmo.rain_moderate", category: "rain" },
	65: { key: "weather.wmo.rain_heavy", category: "rain" },
	66: { key: "weather.wmo.freezing_rain_light", category: "freezing_rain" },
	67: { key: "weather.wmo.freezing_rain_heavy", category: "freezing_rain" },
	71: { key: "weather.wmo.snow_light", category: "snow" },
	73: { key: "weather.wmo.snow_moderate", category: "snow" },
	75: { key: "weather.wmo.snow_heavy", category: "snow" },
	77: { key: "weather.wmo.snow_grains", category: "snow_grains" },
	80: { key: "weather.wmo.rain_showers_light", category: "rain_showers" },
	81: { key: "weather.wmo.rain_showers_moderate", category: "rain_showers" },
	82: { key: "weather.wmo.rain_showers_violent", category: "rain_showers" },
	85: { key: "weather.wmo.snow_showers_light", category: "snow_showers" },
	86: { key: "weather.wmo.snow_showers_heavy", category: "snow_showers" },
	95: { key: "weather.wmo.thunderstorm", category: "thunderstorm" },
	96: { key: "weather.wmo.thunderstorm_hail_light", category: "thunderstorm_hail" },
	99: { key: "weather.wmo.thunderstorm_hail_heavy", category: "thunderstorm_hail" },
};

export const WMO_FALLBACK: WmoEntry = {
	key: "weather.wmo.unknown",
	category: "overcast",
};

export function lookupWmo(code: number): WmoEntry {
	return WMO_CODES[code] ?? WMO_FALLBACK;
}
